import { inject, injectable } from "inversify";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { IMUserStore } from "@whatslly/instant-messaging/IMUserStore";
import { webScriptBackend } from "@whatslly/instant-messaging/webScriptBackend";
import { WebScriptCommands } from "@whatslly/commons/webScriptCommands";
import { ChunkedQueue } from "@whatslly/commons/chunkedQueue";
import type { MessageQueueItem, SerializedMessage } from "@whatslly/types/instant-messaging/messagesEmitter";
import { backgroundClient } from "@whatslly/commons/background/client";
import { BgCommands } from "@whatslly/commons/bgCommands";
import ms from "ms";
import { getCompactQuery, isSyncEligibleQuery, shouldSyncQuery, toMessageQueueItem } from "@whatslly/instant-messaging/messageSyncUtils";
import type { IVoiceVideoEmitter } from "@whatslly/types/instant-messaging/voiceVideoEmitter";
import { InjectVoiceVideoEmitter } from "@whatslly/types/instant-messaging/voiceVideoEmitter";
import type { IMSelectorsUnion } from "@whatslly/types/integrations/im/IMSelectorsTypes";
import type { InstantMessagingService } from "@whatslly/types/instant-messaging/InstantMessagingService";
import { IInstantMessagingServiceSymbol } from "@whatslly/types/instant-messaging/InstantMessagingService";
import { reaction } from "mobx";
import { IMChannelType, InjectChannelType } from "@whatslly/instant-messaging/channelTypes";

declare global {
  interface Window {
    importNamespace?: (module: string) => any;
  }
}

@injectable()
export class VoiceVideoRecordingService {
  private readonly logger: FrontLogger;
  private abortController?: AbortController;

  public sendQueue = new ChunkedQueue<MessageQueueItem>(
    async (threads) => {
      const userPhone = this.instantMessagingService.getMe()?.phone;

      if (!userPhone) {
        return;
      }

      await backgroundClient.send(BgCommands.SAVE_THREADS, {
        userPhone,
        threads,
      });
    },
    { chunkSize: 20, interval: ms("1m") }
  );

  constructor(
    @inject(FrontLogger) private frontLogger: FrontLogger,
    @inject(IMUserStore) protected readonly imUserStore: IMUserStore,
    @inject(InjectVoiceVideoEmitter) private readonly VoiceVideoEmitter: IVoiceVideoEmitter,
    @inject(IInstantMessagingServiceSymbol) private readonly instantMessagingService: InstantMessagingService,
    @inject(InjectChannelType) private readonly channelType: IMChannelType
  ) {
    this.logger = this.frontLogger.getLogger("voice-video-recording-service");
  }

  private static arrayBufferToBinaryString(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    const CHUNK_SIZE = 8192;
    let binary = "";
    for (let i = 0; i < bytes.length; i += CHUNK_SIZE) {
      binary += String.fromCharCode(...bytes.subarray(i, Math.min(i + CHUNK_SIZE, bytes.length)));
    }
    return binary;
  }

  query(data: { isGroup: boolean; name: string; phone: string; userPhone: string | undefined }) {
    return backgroundClient.send(
      BgCommands.QUERY,
      { ...data, searchName: data.name, ts: Date.now(), timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone },
      new AbortController().signal
    );
  }
  private async listenToVoiceVideoRecording() {
    this.abortController = new AbortController();
    const emitter = new this.VoiceVideoEmitter(
      {
        abortSignal: this.abortController.signal,
      },
      this.logger,
      this.instantMessagingService
    );
    for await (const recordedData of emitter) {
      const userPhone = this.instantMessagingService.getMe()?.phone;
      const maxFileSizeBytes = this.imUserStore.user?.uploadFile?.maxFileSizeBytes ?? 5_000_000;
      if (recordedData.data.arrayBuffer.byteLength > maxFileSizeBytes) {
        this.logger.warn(
          { size: recordedData.data.arrayBuffer.byteLength, max: maxFileSizeBytes },
          "enqueueRecordedCall: recording exceeds max file size"
        );
        continue;
      }
      const chatId = recordedData.activeCall.isSentByMe ? recordedData.activeCall.receiverId : recordedData.activeCall.senderId;
      const phone = recordedData.activeCall.isSentByMe ? recordedData.activeCall.receiverPhone : recordedData.activeCall.senderPhone;
      const q = await this.query({
        isGroup: recordedData.activeCall.isGroupMsg,
        name: (recordedData.activeCall.isSentByMe ? recordedData.activeCall.receiverName : recordedData.activeCall.senderName) ?? "",
        phone,
        userPhone,
      }).then(async (query) => {
        if (!query || !isSyncEligibleQuery(query) || !shouldSyncQuery(query, recordedData.activeCall.isGroupMsg)) {
          return null;
        }

        return query;
      });

      if (!q) {
        this.logger.warn("enqueueRecordedCall: no query found");
        continue;
      }

      const compactQuery = getCompactQuery(q);
      if (!compactQuery) {
        this.logger.warn("enqueueRecordedCall: no compact query");
        continue;
      }

      const msgId = recordedData.activeCall.id;
      const shortId = msgId.substring(msgId.length - 6, msgId.length);
      const binary = VoiceVideoRecordingService.arrayBufferToBinaryString(recordedData.data.arrayBuffer);
      const isSentByMe = recordedData.activeCall.isSentByMe;
      const serializedMessageId = `${Boolean(isSentByMe)}_${chatId}_${msgId}`;
      const serialized: SerializedMessage = {
        chatId,
        id: msgId,
        serializedMessageId,
        shortId,
        t: recordedData.activeCall.t,
        timestamp: recordedData.activeCall.t * 1000,
        isSentByMe,
        isGroupMsg: Boolean(recordedData.activeCall.isGroupMsg),
        isSavedContact: false,
        isSpamContact: false,
        type: "call_recording",
        chatType: "callRecording",
        senderName: recordedData.activeCall.senderName,
        senderId: recordedData.activeCall.senderId,
        senderPhone: recordedData.activeCall.senderPhone,
        receiverName: recordedData.activeCall.receiverName,
        receiverId: recordedData.activeCall.receiverId,
        receiverPhone: recordedData.activeCall.receiverPhone,
        notifyName: "",
        authorPhone: "",
        startOfDay: recordedData.activeCall.t,
        latestUpdate: recordedData.activeCall.t,
        media: {
          mimeType: "audio/wav",
          fileName: recordedData.data.filename,
          size: recordedData.data.arrayBuffer.byteLength,
          binary,
        },
      };

      this.sendQueue.enqueue(
        toMessageQueueItem(serialized, {
          _id: shortId,
          contactPhone: phone,
          query: compactQuery,
        })
      );
    }
  }

  get isSyncWhatsappVoiceVideoEnabled() {
    return Boolean(this.imUserStore.user?.sync_whatsapp_voice_video);
  }

  get isWhatsappVoiceVideoEnabled() {
    return Boolean(this.imUserStore.user?.enable_whatsapp_voice_video);
  }

  getWhatsappFlagId(flagName: string) {
    try {
      return window?.importNamespace?.("WAWebABPropsConfigs").ABPropConfigs[flagName][0];
    } catch {
      return null;
    }
  }

  changeWhatsAppVoiceVideo(enableCalling: boolean) {
    const enableWebGroupCallingId = this.getWhatsappFlagId("enable_web_group_calling");
    const enableWebCallingId = this.getWhatsappFlagId("enable_web_calling");
    const enable_unified_call_buttons_in_chat = this.getWhatsappFlagId("enable_unified_call_buttons_in_chat");
    const enable_web_calling_beta_upsell = this.getWhatsappFlagId("enable_web_calling_beta_upsell");
    const configsOverride = [];
    if (enableWebGroupCallingId) {
      configsOverride.push({
        configCode: enableWebGroupCallingId,
        configValue: false,
        configExpoKey: null,
        hasAccessed: true,
      });
    }
    if (enableWebCallingId) {
      configsOverride.push({
        configCode: enableWebCallingId,
        configValue: enableCalling,
        configExpoKey: null,
        hasAccessed: true,
      });
      if (enable_unified_call_buttons_in_chat) {
        configsOverride.push({
          configCode: enable_unified_call_buttons_in_chat,
          configValue: enableCalling,
          configExpoKey: null,
          hasAccessed: true,
        });
      }
      if (enable_web_calling_beta_upsell) {
        configsOverride.push({
          configCode: enable_web_calling_beta_upsell,
          configValue: enableCalling,
          configExpoKey: null,
          hasAccessed: true,
        });
      }
    }
    try {
      if (configsOverride.length === 0) {
        return;
      }
      window?.importNamespace?.("WAWebABPropsCache").bulkCreateOrReplaceABPropConfigs(configsOverride);
    } catch {
      // ignore
    }
  }

  init(selectors: IMSelectorsUnion): void {
    if (this.channelType !== IMChannelType.whatsapp) {
      return;
    }
    setTimeout(() => {
      this.changeWhatsAppVoiceVideo(this.isWhatsappVoiceVideoEnabled);
    });
    if (this.isSyncWhatsappVoiceVideoEnabled) {
      const injected = this.VoiceVideoEmitter.inject(selectors);
      if (injected) {
        this.listenToVoiceVideoRecording();
      }
    }

    reaction(
      () => this.isSyncWhatsappVoiceVideoEnabled,
      (value) => {
        if (value) {
          const injected = this.VoiceVideoEmitter.inject(selectors);
          if (injected) {
            this.listenToVoiceVideoRecording();
          }
        }
      }
    );
    reaction(
      () => this.isWhatsappVoiceVideoEnabled,
      (value) => {
        setTimeout(() => {
          this.changeWhatsAppVoiceVideo(value);
        });
      }
    );
    webScriptBackend.on(WebScriptCommands.LOGOUT, () => {
      this.abortController?.abort();
      this.VoiceVideoEmitter.restoreOriginalWindow();
    });
  }
}
